import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Modal, ModalBody, ModalHeader } from "react-bootstrap";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import PaginationComponent from "../../../components/Pagination";
import ErrorMessage from "../../../components/ErrorMessage";
import { faqAdminContext } from "../../../context/AdminContext/FAQAdminContext/FAQAdminContext";
import { createFAQValidation } from "../../../utils/validation/validation";
import { convertDate, numberSeparate } from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";

const FAQsAdmin = () => {
  const {
    faqs,
    faq,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    pageNumber,
    setPageNumber,
    itemsCount,
    setItemsCount,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetFAQs,
    handleCreateFAQ,
    handleEditFAQ,
    handleDeleteFAQ,
  } = useContext(faqAdminContext);

  useEffect(() => {
    handleGetFAQs();
  }, [start, end, pageNumber, itemsCount, sort]);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createFAQValidation),
  });

  const handleAddItem = (type) => {
    const initialDocuments = {
      question: "",
      answer: "",
    };

    switch (type) {
      case "questions":
        setValue(type, [...(watch(type) ?? []), initialDocuments]);
        break;

      default:
        break;
    }
  };
  const handleRemoveItem = (type, index) =>
    setValue(
      type,
      watch(type)?.filter((_, _i) => _i !== index)
    );

  return (
    <>
      <SiteTitle title="سوالات متداول" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <button
              className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
              onClick={() => {
                handleShowModal();
                reset();
              }}
            >
              <Icon icon="ic:baseline-plus" className="text-xl" />
              افزودن سوالات
            </button>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetFAQs}
        />
        <div className="card-body p-24">
          {isEmpty(faqs) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">سوالات</th>
                      <th scope="col">تعداد</th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col">تاریخ آخرین ویرایش</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {faqs.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>{item.title}</td>
                        <td>{numberSeparate(item.questions?.length)}</td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          {convertDate(item.updatedAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() => {
                                  reset();
                                  handleShowModal(item, setValue);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                ویرایش
                              </li>

                              <li className="dropdown-divider" />

                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteFAQ(item._id),
                                    `آیا از حذف سوالات ${item.title} مطمئنید؟`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(faq) ? "افزودن سوالات جدید" : "ویرایش سوالات"}
          </span>
        </ModalHeader>
        <ModalBody>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(faq) ? handleCreateFAQ : handleEditFAQ
            )}
          >
            <div className="row">
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="title"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      عنوان
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="title"
                      placeholder="عنوان را وارد نمایید"
                      {...register("title")}
                    />
                  </div>

                  <ErrorMessage message={errors.title?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="form-label fw-semibold text-primary-light text-sm mb-8">
                      سوالات
                    </span>

                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => handleAddItem("questions")}
                    >
                      <Icon icon="ic:baseline-plus" className="text-2xxl" />
                    </button>
                  </div>
                  <div>
                    {isEmpty(watch("questions")) ? (
                      <EmptyData title="سوالی برای نمایش یافت نشد" />
                    ) : (
                      <div className="row g-2">
                        {watch("questions")?.map((item, index) => (
                          <Col xs="12" key={item?._id + index}>
                            <div className="input-group" dir="ltr">
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() =>
                                  handleRemoveItem("questions", index)
                                }
                              >
                                <Icon
                                  icon="solar:trash-bin-minimalistic-broken"
                                  className="text-xxl"
                                />
                              </button>
                              <input
                                type="text"
                                placeholder="پاسخ را وارد نمایید"
                                className="form-control radius-8 text-end"
                                {...register(`questions[${index}].answer`)}
                              />
                              <input
                                type="text"
                                placeholder="سوال را وارد نمایید"
                                className="form-control radius-8 text-end"
                                {...register(`questions[${index}].question`)}
                              />
                            </div>
                            {errors.questions && (
                              <>
                                <ErrorMessage
                                  message={
                                    errors.questions[index]?.answer?.message
                                  }
                                />
                                <ErrorMessage
                                  message={
                                    errors.questions[index]?.question?.message
                                  }
                                />
                              </>
                            )}
                          </Col>
                        ))}
                      </div>
                    )}
                    <ErrorMessage message={errors.questions?.message} />
                  </div>
                </div>
              </div>
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(faq) ? "افزودن" : "ویرایش"} سوالات
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default FAQsAdmin;
